import React, { ReactText } from 'react';
import theme from './ResidentialUnitEdit.module.scss';
import {
    ModalBody,
    ModalFooter,
    ResidentialUnitCustomFieldDto,
    Select,
    SelectOption,
    UnitVariableDataType,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import MandatoryIndicator from '@/components/forms/MandatoryIndicator';
import { ValidatingInput, ValidationSummary, ValidationForm } from '@/components/forms/validation';
import { Input } from '@/components/generic-controls';
import { TagInput } from '@/components/TagInput';
import { ProjectTagType, ResidentialUnitExtraData, PricingFieldValue, ProjectTag, ChangedItem } from '@/types';
import { Button } from '@/components/Buttons';
import { useAsyncState, useProjectRoomsEnabled } from '@/utils/hooks';
import { getResidentialUnitGroupsByProjectId } from '@/services/residential-unit-groups';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { toNullableNumber } from '@/utils/html';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { SlideToggle } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/pro-light-svg-icons';

const typeOptions: SelectOption[] = [
    {
        label: <Trans id="ResidentialUnitType.Apartment">Apartment</Trans>,
        value: 'Apartment',
    },
    {
        label: <Trans id="ResidentialUnitType.Detached">Detached</Trans>,
        value: 'Detached',
    },
    {
        label: <Trans id="ResidentialUnitType.SemiDetached">Semi Detached</Trans>,
        value: 'SemiDetached',
    },
    {
        label: <Trans id="ResidentialUnitType.Terraced">Terraced</Trans>,
        value: 'Terraced',
    },
    {
        label: <Trans id="ResidentialUnitType.CommonArea">Common area</Trans>,
        value: 'CommonArea',
    },
    {
        label: <Trans id="ResidentialUnitType.ThreeUnitBuilding">Three-unit building</Trans>,
        value: 'ThreeUnitBuilding',
    },
    {
        label: <Trans id="ResidentialUnitType.Commercial">Commercial</Trans>,
        value: 'Commercial',
    },
];
interface ResidentialUnitEditUnitProps {
    residentialUnit: Partial<ResidentialUnitExtraData>;
    unitVariables: ResidentialUnitCustomFieldDto[];
    onChange(change: ChangedItem<Partial<ResidentialUnitExtraData>>): void;
    pricingFieldValues: PricingFieldValue[] | null;
    setPricingFieldValues(pf: PricingFieldValue[] | null): void;
    setUnitVariables(uv: ResidentialUnitCustomFieldDto[] | null): void;
    selectedTags: ProjectTag[];
    setSelectedTags(tag: ProjectTag[]): void;
    saving: boolean;
    toggleOpen(): void;
    onSubmit(): void;
}
const ResidentialUnitEditUnit: React.FC<ResidentialUnitEditUnitProps> = ({
    residentialUnit,
    onChange,
    pricingFieldValues,
    setPricingFieldValues,
    selectedTags,
    setSelectedTags,
    saving,
    toggleOpen,
    onSubmit,
    unitVariables,
    setUnitVariables,
}) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const roomsEnabled = useProjectRoomsEnabled();
    const handleSelectChange = (nv: { label: any; value: ReactText }, key: 'residentialUnitGroupId' | 'type') => {
        handleFieldChange({ [key]: nv.value });
    };

    const handleFieldChange = (changes: Partial<ResidentialUnitExtraData>) => {
        onChange(ChangedItem.fromMerge(residentialUnit, changes));
    };

    const [residentialUnitGroups] = useAsyncState(() => getResidentialUnitGroupsByProjectId(projectId), [projectId], null);
    const residentialUnitGroupsOptions: SelectOption[] | undefined = residentialUnitGroups?.map((rug) => ({
        label: rug.name,
        value: rug.id,
    }));
    if (residentialUnitGroupsOptions) {
        residentialUnitGroupsOptions.unshift({
            label: <Trans>None</Trans>,
            value: null,
        });
    }

    const handlePricingFieldChange = (e, oldValue) => {
        const value = toNullableNumber(e.target.value);

        setPricingFieldValues(arrayReplace(pricingFieldValues!, oldValue, { ...oldValue, value }));
    };
    const handleUnitVariableChange = (e, oldValue: ResidentialUnitCustomFieldDto) => {
        setUnitVariables(arrayReplace(unitVariables, oldValue, { ...oldValue, value: e.target.value }));
    };

    const handleUnitVariableBooleanChange = (e, oldValue: ResidentialUnitCustomFieldDto) => {
        setUnitVariables(arrayReplace(unitVariables, oldValue, { ...oldValue, value: e.target.checked ? 'true' : 'false' }));
    };

    const handleFormSubmit = (e) => {
        e.stopPropagation();
        onSubmit();
    };

    const getInputType = (dataType: UnitVariableDataType) => {
        switch (dataType) {
            case UnitVariableDataType.NUMBER:
                return 'number';
            case UnitVariableDataType.DATE:
                return 'date';
            case UnitVariableDataType.BOOLEAN:
                return 'checkbox';
            default:
                return 'text';
        }
    };
    return (
        <ValidationForm onSubmit={handleFormSubmit}>
            <ModalBody>
                <div className="form labels-30">
                    <div>
                        <label>
                            <Trans>Residential unit name</Trans>
                            <MandatoryIndicator />

                            <ValidatingInput
                                required={true}
                                type="text"
                                className="form-control"
                                placeholder={t`Residential unit name`}
                                value={residentialUnit.name}
                                onChange={(e) => handleFieldChange({ name: e.target.value })}
                            />
                        </label>
                    </div>
                    <div>
                        <label>
                            <Trans>Residential unit type</Trans>
                            <Select
                                className="form-control-unstyled"
                                defaultValue={typeOptions[0]}
                                onChange={(nv: any) => handleSelectChange(nv, 'type')}
                                options={typeOptions}
                                value={typeOptions.find((to) => to.value === residentialUnit.type)}
                            />
                        </label>
                    </div>
                    {residentialUnitGroupsOptions && (
                        <div>
                            <label>
                                <Trans>Residential unit group</Trans>
                                <Select
                                    defaultValue={residentialUnitGroupsOptions[0]}
                                    className="form-control-unstyled"
                                    options={residentialUnitGroupsOptions}
                                    onChange={(nv: any) => handleSelectChange(nv, 'residentialUnitGroupId')}
                                    value={residentialUnitGroupsOptions.find(
                                        (rug) => rug.value === residentialUnit.residentialUnitGroupId
                                    )}
                                />
                            </label>
                        </div>
                    )}
                    <div>
                        <label>
                            <Trans>Floor</Trans>
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder={t`Floor`}
                                name="floor"
                                value={residentialUnit.floor || ''}
                                onChange={(e) => handleFieldChange({ floor: e.target.value })}
                            />
                        </label>
                    </div>
                    <div>
                        <label>
                            <Trans>Layout type</Trans>
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder={t`Layout type`}
                                name="layoutType"
                                value={residentialUnit.layoutType || ''}
                                onChange={(e) => handleFieldChange({ layoutType: e.target.value })}
                            />
                        </label>
                    </div>
                    <div>
                        <label>
                            <Trans>Address</Trans>
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder={t`Address`}
                                name="address"
                                value={residentialUnit.address || ''}
                                onChange={(e) => handleFieldChange({ address: e.target.value })}
                            />
                        </label>
                    </div>
                    {pricingFieldValues && pricingFieldValues.length > 0 && (
                        <>
                            <hr />
                            <div className={theme.header}>
                                <Trans>Pricing variables</Trans>
                            </div>
                            {pricingFieldValues.map((pricingValue) => (
                                <div key={pricingValue.pricingFieldId}>
                                    <label>
                                        {pricingValue.name}
                                        <div className="form-control-unstyled">
                                            <Input
                                                type="number"
                                                className="form-control no-spinner text-right"
                                                value={pricingValue.value == null ? '' : pricingValue.value}
                                                style={{ width: '100px' }}
                                                onChange={(e) => handlePricingFieldChange(e, pricingValue)}
                                            />
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </>
                    )}

                    {unitVariables && unitVariables.length > 0 && (
                        <>
                            <hr />
                            <div className={theme.header}>
                                <Trans>Unit variables</Trans>
                            </div>
                            <div className="form labels-30">
                                {unitVariables.map((uv) => (
                                    <div key={uv.id}>
                                        <label>
                                            {uv.type.iconName && (
                                                <FontAwesomeIcon
                                                    className={theme.icon}
                                                    icon={['fal', uv.type.iconName as IconName]}
                                                />
                                            )}
                                            {uv.type.name}
                                            {uv.type.dataType === UnitVariableDataType.BOOLEAN ? (
                                                <SlideToggle
                                                    className="form-control-unstyled"
                                                    checked={uv.value === 'true'}
                                                    onChange={(e) => handleUnitVariableBooleanChange(e, uv)}
                                                />
                                            ) : (
                                                <Input
                                                    className="form-control"
                                                    type={getInputType(uv.type.dataType)}
                                                    value={uv.value == null ? '' : uv.value}
                                                    onChange={(e) => handleUnitVariableChange(e, uv)}
                                                />
                                            )}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    <hr />
                    <div>
                        <label>
                            <Trans>Unit tags</Trans>
                            <div className="form-control-unstyled">
                                <TagInput
                                    placeholder={<Trans>Select tags</Trans>}
                                    value={selectedTags.filter((t) => t.type !== ProjectTagType.NewRoom)}
                                    className={'full-width'}
                                    type={ProjectTagType.Room}
                                    projectId={projectId}
                                    canCreate
                                    onTagsChange={(e) =>
                                        setSelectedTags([
                                            ...e,
                                            ...selectedTags.filter((t) => t.type === ProjectTagType.NewRoom),
                                        ])
                                    }
                                />
                            </div>
                        </label>
                    </div>
                    {roomsEnabled && (
                        <div>
                            <label>
                                <Trans>Rooms</Trans>
                                <div className="form-control-unstyled">
                                    <TagInput
                                        value={selectedTags.filter((t) => t.type === ProjectTagType.NewRoom)}
                                        className="full-width"
                                        type={ProjectTagType.NewRoom}
                                        projectId={projectId}
                                        onTagsChange={(e) =>
                                            setSelectedTags([
                                                ...e,
                                                ...selectedTags.filter((t) => t.type !== ProjectTagType.NewRoom),
                                            ])
                                        }
                                    />
                                </div>
                            </label>
                        </div>
                    )}
                    <div className="form-label">
                        <div className="form-control-unstyled">
                            <SlideToggle
                                onChange={(e) =>
                                    handleFieldChange({
                                        isReadOnlyForBuyers: e.target.checked,
                                    })
                                }
                                checked={residentialUnit.isReadOnlyForBuyers}>
                                <Trans>Read only</Trans>
                            </SlideToggle>
                        </div>
                    </div>
                    <div className="label-offset">
                        <ValidationSummary />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button onClick={toggleOpen} color="tertiary" disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>

                <Button type="submit" color="primary" loading={saving}>
                    {residentialUnit.id ? <Trans>Update</Trans> : <Trans>Create</Trans>}
                </Button>
            </ModalFooter>
        </ValidationForm>
    );
};

export default ResidentialUnitEditUnit;
